import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { TaskStatus } from "@common/taskStatus";
import { UseCases } from "@planzone/core";
import { ProjectElementType } from "@planzone/dto";
import { useEditTask } from "@planzone/view-models";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import { useModal } from "@root/client/modal";
import { ActivityWatcher } from "@common/activityWatcher";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { CommentsCount } from "../../commentsCount";
import { Props } from "./types";
import { AllocationsList } from "./allocationsList";
import { NameEditionForm } from "./nameEditionForm";
import styles from "./styles.scss";

function Task({ task, project, list }: Props) {
  const isLate = !!task.endDate && new Date(task.endDate) < new Date();
  const isDone =
    task.percentComplete === 100 || task.status.percentComplete === 100;
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { editTask } = useEditTask(task);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.task, { [styles.late]: isLate && !isDone })}
    >
      <div
        className={styles.taskDetails}
        onClick={() =>
          pushView({
            key: UseCases.retrieveTask,
            data: { task },
          })
        }
      >
        <div className={styles.name}>
          <div
            className={styles.taskCompletionStatus}
            onClick={
              !project.isTemplate
                ? (e) => {
                    e.stopPropagation();
                    !isDone
                      ? pushView({
                          key: UseCases.markTaskAsCompleted,
                          data: { task, project },
                        })
                      : editTask({
                          task,
                          nextTask: { ...task, percentComplete: 0 },
                        });
                  }
                : undefined
            }
          >
            <TaskCompletionStatus
              progress={task.percentComplete}
              endDate={task.endDate}
              task={task}
            />
          </div>
          <NameEditionForm task={task} />
        </div>
        <div className={styles.taskStatus}>
          <TaskStatus status={task.status} />
        </div>
        <div className={styles.allocations}>
          <AllocationsList
            allocations={task.allocations}
            task={task}
            project={project}
          />
        </div>
        <div className={styles.endDate}>
          {!!task.endDate && (
            <p className={styles.text}>
              {new Date(task.endDate).toLocaleDateString()}
            </p>
          )}
          {isDone &&
            (!!task.endDate ? (
              <p className={styles.completionDate}>
                ({t("projectPage.listsPage.completionDate")}:{" "}
                {new Date(task.completionDate).toLocaleDateString()})
              </p>
            ) : (
              <p className={styles.completionDate}>
                {new Date(task.completionDate).toLocaleDateString()}
              </p>
            ))}
        </div>
        <CommentsCount
          subject={task.uuid}
          type="task"
          commentsNbr={task.commentsNbr}
        />
      </div>
      <div
        className={classNames(styles.contextualMenu, {
          [styles.displayedContextualMenu]: showContextualMenu,
        })}
      >
        <div ref={ref}>
          <SecureContextualMenu
            context={{ task }}
            replacement={<div className={styles.placeholder} />}
            items={[
              {
                name: t("projectPage.tasksContextualMenu.edit"),
                useCase: UseCases.editTask,
                act: () =>
                  pushView({
                    data: { task },
                    key: UseCases.retrieveTask,
                  }),
              },
              {
                name: t("projectPage.tasksContextualMenu.move"),
                useCase: UseCases.moveListElement,
                act: () =>
                  pushView({
                    key: UseCases.moveListElement,
                    data: {
                      projectElementId: task.uuid,
                      projectElementType: ProjectElementType.TASK,
                      currentParentList: list,
                      project,
                    },
                  }),
              },
              {
                name: t("projectPage.tasksContextualMenu.duplicate"),
                useCase: UseCases.createTask,
                act: () =>
                  pushView({
                    data: { project, list, task },
                    key: UseCases.createTask,
                  }),
              },
              {
                name: t("projectPage.tasksContextualMenu.remove"),
                useCase: UseCases.removeTask,
                color: "#db5f63",
                act: () =>
                  pushView({
                    data: task,
                    key: UseCases.removeTask,
                  }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export { Task };
